import React, { useEffect, useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import GironeBadge from "@/components/GironeBadge";
import { api } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Coins, Search, Wallet } from "lucide-react";

const ROLE_COLORS = { PT: "#f59e0b", DEF: "#06b6d4", CEN: "#3b56e0", ATT: "#f97316" };

export default function Aste() {
  const [data, setData] = useState({ A: [], B: [], C: [], D: [] });
  const [budget, setBudget] = useState([]);
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("ALL");

  useEffect(() => {
    api.get("/public/asta/players").then(({ data }) => setData(data || {})).catch(() => {});
    api.get("/public/asta/budget").then(({ data }) => setBudget(data || [])).catch(() => {});
  }, []);

  const filter = (arr) => {
    const s = q.trim().toLowerCase();
    return arr.filter((p) =>
      (roleFilter === "ALL" || p.role === roleFilter) &&
      (s === "" || p.name?.toLowerCase().includes(s) || p.team?.toLowerCase().includes(s))
    );
  };

  return (
    <div className="App">
      <Header />
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-10 space-y-6">
        <div className="fade-up flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background: "#3b56e022", color: "#3b56e0" }}>
            <Coins className="w-6 h-6" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500">Aste 2026/27</div>
            <h1 className="display font-black text-4xl md:text-5xl">Aste</h1>
          </div>
        </div>

        {/* Confronto budget: grafico bar con crediti residui */}
        <BudgetChart rows={budget} />

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2" data-testid="aste-filters">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca giocatore o squadra…"
                   className="h-10 pl-9 bg-white border-slate-200 rounded-full" data-testid="aste-search" />
          </div>
          <div className="flex gap-1.5">
            {["ALL", "PT", "DEF", "CEN", "ATT"].map((r) => (
              <button key={r} type="button" onClick={() => setRoleFilter(r)}
                      data-testid={`aste-role-${r}`}
                      className={`h-10 px-3 rounded-full text-[11px] font-black uppercase tracking-widest transition
                        ${roleFilter === r ? "text-white shadow-sm" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                      style={roleFilter === r ? { background: r === "ALL" ? "#0f172a" : ROLE_COLORS[r] } : undefined}>
                {r === "ALL" ? "Tutti" : r}
              </button>
            ))}
          </div>
        </div>

        <Tabs defaultValue="A">
          <TabsList className="bg-slate-50 border border-slate-200 w-full sm:w-auto grid grid-cols-4 sm:inline-flex">
            {["A", "B", "C", "D"].map((g) => (
              <TabsTrigger key={g} value={g} className="data-[state=active]:bg-[var(--brand)] data-[state=active]:text-white inline-flex items-center gap-1.5 px-2 sm:px-3"
                           data-testid={`aste-tab-${g}`}>
                <GironeBadge girone={g} size={18} />
                <span className="hidden sm:inline">Girone</span>
              </TabsTrigger>
            ))}
          </TabsList>
          {["A", "B", "C", "D"].map((g) => {
            const rows = filter(data[g] || []);
            return (
              <TabsContent key={g} value={g} className="mt-6">
                <div className="card-surface rounded-2xl overflow-hidden" data-testid={`aste-table-${g}`}>
                  <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-500">Girone {g}</div>
                      <div className="display font-black text-lg">{rows.length} giocatori</div>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-widest">
                        <tr>
                          <th className="px-3 py-3 text-left w-12">#</th>
                          <th className="px-3 py-3 text-left">Giocatore</th>
                          <th className="px-3 py-3 text-center w-16">Ruolo</th>
                          <th className="px-3 py-3 text-left">Squadra</th>
                          <th className="px-3 py-3 text-right w-24">Costo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.length === 0 && (
                          <tr><td colSpan="5" className="px-3 py-8 text-center text-slate-400 italic">Nessun giocatore corrisponde ai filtri.</td></tr>
                        )}
                        {rows.map((p, i) => (
                          <tr key={`${p.name}-${p.team}-${i}`} className="border-t border-slate-100 hover:bg-slate-50"
                              data-testid={`aste-row-${g}-${i}`}>
                            <td className="px-3 py-2 text-slate-500 font-bold">{i + 1}</td>
                            <td className="px-3 py-2 text-slate-900 font-semibold">{p.name}</td>
                            <td className="px-3 py-2 text-center">
                              <span className="text-[10px] font-black px-2 py-0.5 rounded" style={{ background: `${ROLE_COLORS[p.role]}22`, color: ROLE_COLORS[p.role] }}>{p.role}</span>
                            </td>
                            <td className="px-3 py-2 text-slate-600 truncate">{p.team}</td>
                            <td className="px-3 py-2 text-right font-black tabular-nums" style={{ color: p.cost >= 30 ? "var(--orange)" : "var(--brand)" }}>
                              {p.cost}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </TabsContent>
            );
          })}
        </Tabs>
      </div>
      <Footer />
    </div>
  );
}

function BudgetChart({ rows }) {
  const [gFilter, setGFilter] = useState("ALL");
  if (!rows || rows.length === 0) return null;
  const filtered = gFilter === "ALL" ? rows : rows.filter((r) => r.girone === gFilter);
  const budget = rows[0]?.budget || 450;
  const maxRes = Math.max(...filtered.map((r) => r.residual), 1);
  const gironeAccent = { A: "#3b56e0", B: "#f97316", C: "#06b6d4", D: "#a855f7" };
  return (
    <div className="card-surface rounded-2xl p-5" data-testid="budget-chart">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "#22c55e18", color: "#16a34a" }}>
            <Wallet className="w-5 h-5" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Confronto Budget · {budget} crediti iniziali</div>
            <div className="display font-black text-lg md:text-xl">Crediti residui per squadra</div>
          </div>
        </div>
        <div className="flex gap-1.5" data-testid="budget-girone-filter">
          {["ALL", "A", "B", "C", "D"].map((g) => (
            <button key={g} type="button" onClick={() => setGFilter(g)}
                    data-testid={`budget-girone-${g}`}
                    className={`h-8 px-3 rounded-full text-[10px] font-black uppercase tracking-widest transition
                      ${gFilter === g ? "text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                    style={gFilter === g ? { background: g === "ALL" ? "#0f172a" : gironeAccent[g] } : undefined}>
              {g === "ALL" ? "Tutti" : g}
            </button>
          ))}
        </div>
      </div>
      <div className="space-y-2 sm:space-y-2">
        {filtered.map((r) => {
          const pct = Math.round((r.residual / maxRes) * 100);
          const spentPct = Math.round((r.spent / (r.budget || 450)) * 100);
          const critical = r.residual <= 5;
          return (
            <div key={r.team_id}
                 className="grid grid-cols-[1fr_auto] sm:grid-cols-[auto_1fr_auto] items-center gap-2 sm:gap-3 py-1"
                 data-testid={`budget-row-${r.team_id}`}>
              {/* Team badge + name */}
              <div className="flex items-center gap-2 min-w-0 sm:w-[180px] sm:shrink-0 order-1">
                <GironeBadge girone={r.girone} size={18} />
                <span className="text-sm font-semibold text-slate-800 truncate" title={r.team_name}>{r.team_name}</span>
              </div>
              {/* Residual (mobile: appare a destra sopra la barra; desktop: colonna dedicata) */}
              <div className="w-20 sm:w-24 text-right order-2 sm:order-3">
                <div className="display font-black tabular-nums text-base sm:text-lg leading-none"
                     style={{ color: critical ? "#dc2626" : "#16a34a" }}>
                  {r.residual}
                </div>
                <div className="text-[9px] sm:text-[10px] uppercase tracking-widest text-slate-500">residui · {100 - spentPct}%</div>
              </div>
              {/* Progress bar (mobile: seconda riga full-width; desktop: colonna centrale) */}
              <div className="relative h-6 bg-slate-100 rounded-full overflow-hidden col-span-2 sm:col-span-1 sm:order-2"
                   title={`Spesi ${r.spent} · Residui ${r.residual}`}>
                <div className="absolute inset-y-0 left-0 rounded-full transition-all"
                     style={{ width: `${pct}%`, background: critical ? "linear-gradient(90deg,#f97316,#dc2626)" : "linear-gradient(90deg,#22c55e,#16a34a)" }} />
                <div className="absolute inset-0 flex items-center justify-end px-3 text-[11px] font-black text-slate-700 tabular-nums">
                  <span className="text-slate-500 mr-1">spesi {r.spent}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

